import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Dialog } from '../../../components/Dialog';

export type DateFilterValue = 'today' | 'tomorrow' | 'day-after-tomorrow' | 'yesterday' | 'day-before-yesterday' | 'this-week' | 'this-month' | 'custom' | 'all';

export interface DateRange {
  from: string;
  to: string;
}

interface DateFilterProps {
  value: DateFilterValue;
  customRange?: DateRange;
  onChange: (value: DateFilterValue, customRange?: DateRange) => void;
}

export const DateFilter: React.FC<DateFilterProps> = ({ value, customRange, onChange }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [tempRange, setTempRange] = useState<DateRange>(
    customRange || { from: '', to: '' }
  );
  const [dropdownPosition, setDropdownPosition] = useState<{
    alignRight: boolean;
    alignTop: boolean;
  }>({ alignRight: false, alignTop: false });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filters = [
    { id: 'all', label: 'Todas las fechas' },
    { id: 'day-before-yesterday', label: 'Antes de ayer' },
    { id: 'yesterday', label: 'Ayer' },
    { id: 'today', label: 'Hoy' },
    { id: 'tomorrow', label: 'Mañana' },
    { id: 'day-after-tomorrow', label: 'Pasado mañana' },
    { id: 'this-week', label: 'Esta semana' },
    { id: 'this-month', label: 'Este mes' },
    { id: 'custom', label: 'Rango personalizado' },
  ];

  // Calcular posición del dropdown cuando se abre
  useEffect(() => {
    if (showDropdown && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Determinar si hay espacio a la derecha
      const spaceRight = viewportWidth - buttonRect.right;
      const spaceLeft = buttonRect.left;
      const alignRight = spaceRight < dropdownRect.width && spaceLeft > spaceRight;

      // Determinar si hay espacio abajo
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const alignTop = spaceBelow < dropdownRect.height && spaceAbove > spaceBelow;

      setDropdownPosition({ alignRight, alignTop });
    }
  }, [showDropdown]);

  const getLabel = () => {
    const filter = filters.find(f => f.id === value);
    if (value === 'custom' && customRange) {
      if (customRange.from === customRange.to) {
        return new Date(customRange.from).toLocaleDateString('es-ES');
      }
      return `${new Date(customRange.from).toLocaleDateString('es-ES')} - ${new Date(customRange.to).toLocaleDateString('es-ES')}`;
    }
    return filter?.label || 'Filtrar por fecha';
  };

  const handleSelect = (id: DateFilterValue) => {
    if (id === 'custom') {
      setShowCustom(true);
      setShowDropdown(false);
    } else {
      onChange(id);
      setShowDropdown(false);
      setShowCustom(false);
    }
  };

  const handleApplyCustom = () => {
    if (tempRange.from && tempRange.to) {
      onChange('custom', tempRange);
      setShowCustom(false);
    }
  };

  const handleClear = () => {
    onChange('all');
    setShowDropdown(false);
    setShowCustom(false);
    setTempRange({ from: '', to: '' });
  };

  // ── Navegación día a día ───────────────────────────────────────────────────
  const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (s: string): Date => {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d); // medianoche local
  };

  const startOfToday = (): Date => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  };

  // Devuelve el día único seleccionado (Date a medianoche local) o null si la
  // selección es un rango ('this-week', 'this-month', custom con from≠to) o sin
  // fecha ('all'). Las flechas solo se muestran cuando esto no es null.
  const getSelectedSingleDay = (): Date | null => {
    const today = startOfToday();
    const shift = (n: number) => {
      const d = new Date(today);
      d.setDate(d.getDate() + n);
      return d;
    };
    switch (value) {
      case 'today': return today;
      case 'tomorrow': return shift(1);
      case 'day-after-tomorrow': return shift(2);
      case 'yesterday': return shift(-1);
      case 'day-before-yesterday': return shift(-2);
      case 'custom':
        return customRange && customRange.from && customRange.from === customRange.to
          ? parseLocalDate(customRange.from)
          : null;
      default: return null; // this-week, this-month, all
    }
  };

  const selectedSingleDay = getSelectedSingleDay();
  const isSingleDay = selectedSingleDay !== null;

  // Mueve la selección ±1 día. Si el día destino coincide con un preset relativo
  // a hoy (ayer/hoy/mañana…) usamos ese preset para conservar la etiqueta; si
  // no, caemos a un rango 'custom' de un solo día (from === to).
  const navigateDay = (delta: number) => {
    if (!selectedSingleDay) return;
    const target = new Date(selectedSingleDay);
    target.setDate(target.getDate() + delta);

    const diffDays = Math.round((target.getTime() - startOfToday().getTime()) / 86_400_000);
    const presetByDiff: Record<number, DateFilterValue> = {
      [-2]: 'day-before-yesterday',
      [-1]: 'yesterday',
      [0]: 'today',
      [1]: 'tomorrow',
      [2]: 'day-after-tomorrow',
    };
    const preset = presetByDiff[diffDays];
    if (preset) {
      onChange(preset);
    } else {
      const s = formatDate(target);
      onChange('custom', { from: s, to: s });
    }
  };

  return (
    <div className="flex items-center gap-1">
      {isSingleDay && (
        <button
          onClick={() => navigateDay(-1)}
          aria-label="Día anterior"
          title="Día anterior"
          className="flex items-center justify-center w-9 h-9 rounded-lg border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
      )}
      <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
          value === 'all'
            ? 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            : 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
        }`}
      >
        <Calendar size={18} />
        <span className="text-sm font-medium">{getLabel()}</span>
        {value !== 'all' ? (
          <span
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="hover:bg-green-200 rounded p-0.5 cursor-pointer inline-flex"
          >
            <X size={16} />
          </span>
        ) : (
          <ChevronDown size={16} />
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowDropdown(false)}
          />
          <div 
            ref={dropdownRef}
            className={`absolute mt-2 w-56 max-h-[calc(100vh-100px)] overflow-y-auto bg-white rounded-lg shadow-lg border border-slate-200 z-20 ${
              dropdownPosition.alignRight ? 'right-0' : 'left-0'
            } ${
              dropdownPosition.alignTop ? 'bottom-full mb-2 mt-0' : ''
            }`}
          >
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => handleSelect(filter.id as DateFilterValue)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${
                  value === filter.id
                    ? 'bg-green-50 text-green-700 font-medium'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </>
      )}

      {/* Custom Range Dialog */}
      <Dialog 
        isOpen={showCustom} 
        onClose={() => setShowCustom(false)}
        title="Seleccionar rango de fechas"
        size="sm"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Desde
            </label>
            <input
              type="date"
              value={tempRange.from}
              onChange={(e) => setTempRange({ ...tempRange, from: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Hasta
            </label>
            <input
              type="date"
              value={tempRange.to}
              onChange={(e) => setTempRange({ ...tempRange, to: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button
            onClick={() => setShowCustom(false)}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 font-medium hover:bg-slate-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleApplyCustom}
            disabled={!tempRange.from || !tempRange.to}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Aplicar
          </button>
        </div>
      </Dialog>
      </div>
      {isSingleDay && (
        <button
          onClick={() => navigateDay(1)}
          aria-label="Día siguiente"
          title="Día siguiente"
          className="flex items-center justify-center w-9 h-9 rounded-lg border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 transition-colors"
        >
          <ChevronRight size={18} />
        </button>
      )}
    </div>
  );
};
